import { Component, computed, inject, input, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { Router } from '@angular/router';
import { RestService } from '@cumlaude/shared-services';
import { filter, switchMap, timer } from 'rxjs';
import { InstellingETLStatus, RJobrun, RJobrunDetail, RJobrunInstelling, Workflow } from '@cumlaude/service-contract';
import { DatePipe, NgClass } from '@angular/common';
import { isUndefined } from 'lodash-es';
import { doorlooptijd, doorlooptijdInMs } from '../../services/etl.service';

type SortColumn = 'Naam' | 'Status' | 'Bulk' | 'Starttijd' | 'Eindtijd' | 'Doorlooptijd';
type SortDirection = 'asc' | 'desc';

@Component({
	selector: 'app-etl-details',
	templateUrl: './etl-details.component.html',
	styleUrls: ['./etl-details.component.scss'],
	imports: [ButtonComponent, DatePipe, NgClass, IconComponent],
})
export class EtlDetailsComponent {
	private readonly router = inject(Router);
	private readonly restService = inject(RestService);

	readonly uid = input<string | undefined>(undefined);

	uid$ = toObservable(this.uid);

	available$ = this.restService.isArgoWorkflowsAvailable();
	readonly available = toSignal(this.available$);

	readonly workflows = toSignal(
		this.available$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => timer(0, 60 * 1000)),
			switchMap(() => this.restService.getWorkflows())
		),
		{
			initialValue: [],
		}
	);

	readonly currentWorkflow = computed<Workflow | undefined>(() => this.workflows().find((workflow) => workflow.metadata.uid === this.uid()));

	readonly workflowOwner = computed(() => {
		const currentWorkflow = this.currentWorkflow();
		if (!currentWorkflow) return undefined;

		const ownerReferences = currentWorkflow.metadata.ownerReferences;
		if (!ownerReferences || ownerReferences.length === 0) return undefined;

		switch (ownerReferences[0].kind) {
			case 'WorkflowTemplate':
				return 'template';
			case 'CronWorkflow':
				return 'cron job';
			default:
				return undefined;
		}
	});

	readonly jobAudits = toSignal(
		timer(0, 60 * 1000).pipe(
			switchMap(() => this.uid$),
			switchMap((uid) => this.restService.getJobAudits({ workflowUid: uid }))
		),
		{
			initialValue: [],
		}
	);

	readonly workflowStarter = computed(() => {
		const currentWorkflow = this.currentWorkflow();
		if (!currentWorkflow) return undefined;

		const ownerReferences = currentWorkflow.metadata.ownerReferences;
		if (!ownerReferences || ownerReferences.length === 0) return undefined;

		switch (ownerReferences[0].kind) {
			case 'WorkflowTemplate':
				return this.jobAudits().at(0)?.createdBy ?? 'Onbekend';
			case 'CronWorkflow':
				return 'cron schedule';
			default:
				return undefined;
		}
	});

	readonly relatedWorkflows = computed<Workflow[]>(() => {
		const currentWorkflow = this.currentWorkflow();
		if (!currentWorkflow) return [];

		const ownerReferences = currentWorkflow.metadata.ownerReferences;
		if (!ownerReferences || ownerReferences.length === 0) return [];

		const owner = ownerReferences[0];
		return this.workflows().filter((workflow) => workflow.metadata.ownerReferences?.some((ownerRef) => ownerRef.uid === owner.uid));
	});

	readonly jobruns: Signal<RJobrun[]> = toSignal(
		timer(0, 60 * 1000).pipe(
			switchMap(() => this.uid$),
			switchMap((uid) => this.restService.getJobruns({ workflowUid: uid }))
		),
		{
			initialValue: [],
		}
	);

	readonly jobrunInstellingen: Signal<RJobrunInstelling[]> = toSignal(
		timer(0, 60 * 1000).pipe(
			switchMap(() => this.uid$),
			switchMap((uid) => this.restService.getJobrunInstellingen({ workflowUid: uid }))
		),
		{ initialValue: [] }
	);

	protected readonly sortableColumns: SortColumn[] = ['Naam', 'Status', 'Bulk', 'Starttijd', 'Eindtijd', 'Doorlooptijd'];

	readonly sortState = signal<{ column: SortColumn; direction: SortDirection }>({ column: 'Naam', direction: 'asc' });

	readonly sortedJobrunInstellingen = computed<RJobrunInstelling[]>(() => {
		const { column, direction } = this.sortState();
		const factor = direction === 'asc' ? 1 : -1;
		return [...this.jobrunInstellingen()].sort((a, b) => {
			const valueA = this.sortValue(a, column);
			const valueB = this.sortValue(b, column);

			// Lege waarden sorteren altijd achteraan, ongeacht de sorteerrichting.
			if (valueA === undefined && valueB === undefined) return 0;
			if (valueA === undefined) return 1;
			if (valueB === undefined) return -1;

			const compare = typeof valueA === 'string' ? valueA.localeCompare(valueB as string) : valueA - (valueB as number);
			return factor * compare;
		});
	});

	sort(column: SortColumn) {
		this.sortState.update(({ column: current, direction }) => {
			if (current !== column) return { column, direction: 'asc' };
			return { column, direction: direction === 'asc' ? 'desc' : 'asc' };
		});
	}

	private sortValue(jobrunInstelling: RJobrunInstelling, column: SortColumn): string | number | undefined {
		switch (column) {
			case 'Naam':
				return jobrunInstelling.instelling.naam;
			case 'Status':
				return Object.values(InstellingETLStatus).indexOf(jobrunInstelling.instellingETLStatus);
			case 'Bulk':
				return jobrunInstelling.bulkload;
			case 'Starttijd':
				return jobrunInstelling.vanaf ? new Date(jobrunInstelling.vanaf).getTime() : undefined;
			case 'Eindtijd':
				return jobrunInstelling.totMet ? new Date(jobrunInstelling.totMet).getTime() : undefined;
			case 'Doorlooptijd':
				return doorlooptijdInMs(jobrunInstelling.vanaf, jobrunInstelling.totMet);
		}
	}

	readonly instellingOngebondenJobrunDetails = computed<RJobrunDetail[]>(() => {
		return this.jobruns()
			.flatMap((jobrun) => jobrun.instellingOngebondenJobrunDetails)
			.filter((jobrunDetail) => !isUndefined(jobrunDetail))
			.sort((a, b) => {
				if (a?.stapVa === undefined && b?.stapVa === undefined) return 0;
				if (a?.stapVa === undefined) return 1;
				if (b?.stapVa === undefined) return -1;
				return a.stapVa.localeCompare(b.stapVa);
			});
	});

	goBack(): void {
		this.router.navigate(['etl']);
	}

	switchWorkflow(workflow: Workflow) {
		this.router.navigate(['etl', workflow.metadata.uid]);
	}

	soortFromName(name: string) {
		const parts = name.split('-');
		const soort = [];
		for (const part of parts) {
			if (['manual', 'cron'].includes(part)) break;

			soort.push(part);
		}
		return soort.join('-');
	}

	typeFromName(name: string) {
		return name.split('-').find((part) => ['manual', 'cron'].includes(part))!;
	}

	openGrafana(pod: string | undefined, from: string | undefined, to: string | undefined) {
		if (!from) return undefined;

		const fromTimestamp = new Date(from).getTime();
		const toTimestamp = to ? new Date(to).getTime() : Date.now();

		const urlParams = String.raw`{"datasource":"bbxiuuoVz","queries":[{"refId":"A","expr":"{pod=\"${pod}\"}","queryType":"range","datasource":{"type":"loki","uid":"bbxiuuoVz"},"editorMode":"code"}],"range":{"from":"${fromTimestamp}","to":"${toTimestamp}"}}`;
		window.open(`https://grafana.topicus.education/explore?orgId=1&left=${encodeURIComponent(urlParams)}`, '_blank');
	}

	protected readonly InstellingETLStatus = InstellingETLStatus;
	protected readonly doorlooptijd = doorlooptijd;
}
